import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Search, MessageCircle } from 'lucide-react'
import api from '../api/client'
import Pagination from './Pagination'
import SearchableSelect from './SearchableSelect'
import CamposBuilder from './CamposBuilder'
import ItensBuilder from './ItensBuilder'
import ImageField from './ImageField'
import { confirmModal, successModal } from './ui'

// CRUD genérico com PAGINAÇÃO SERVER-SIDE (estilo original).
// config: { title, endpoint, columns, fields, readOnly?, searchable?, idKey?, listKey?,
//           pageSize?, fixedParams? }  fixedParams = filtro fixo p/ list + injetado no create.
export default function Crud({ title, endpoint, columns, fields = [], readOnly = false,
                              searchable = true, idKey = 'id', listKey = 'data', pageSize = 20,
                              fixedParams = {} }) {
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(pageSize)
  const [meta, setMeta] = useState({ total: 0, pages: 1 })
  const [edit, setEdit] = useState(null)
  const [erro, setErro] = useState('')
  const [cepMsg, setCepMsg] = useState('')
  const [dynOpts, setDynOpts] = useState({})  // opções de select carregadas de um endpoint (f.optionsUrl)
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null')?.perfil === 'admin' } catch { return false } })()
  const visibleFields = fields.filter((f) => !f.adminOnly || isAdmin)

  async function buscarCep(raw) {
    const cep = (raw || '').replace(/\D/g, '')
    if (cep.length !== 8) return
    setCepMsg('Buscando endereço…')
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const d = await r.json()
      if (d.erro) { setCepMsg('CEP não encontrado'); return }
      setEdit((e) => ({
        ...e,
        endereco: d.logradouro || e.endereco,
        bairro: d.bairro || e.bairro,
        cidade: d.localidade || e.cidade,
        estado: d.uf || e.estado,
      }))
      setCepMsg('Endereço preenchido ✓')
    } catch { setCepMsg('') }
  }

  // ── Busca de pessoa por CPF/CNPJ (evita duplicar cliente/licenciado) ──
  const [cpfInfo, setCpfInfo] = useState(null)
  const entidade = endpoint.includes('licenciado') ? 'licenciado' : endpoint.includes('cliente') ? 'cliente' : null

  function prefillPessoa(p) {
    setEdit((e) => ({
      ...e, nome: p.nome || e.nome, email: p.email || e.email, telefone: p.telefone || e.telefone,
      whatsapp: p.whatsapp || e.whatsapp, data_nascimento: p.data_nascimento || e.data_nascimento,
      cep: p.cep || e.cep, endereco: p.endereco || e.endereco, numero: p.numero || e.numero,
      complemento: p.complemento || e.complemento, bairro: p.bairro || e.bairro,
      cidade: p.cidade || e.cidade, estado: p.estado || e.estado,
    }))
  }

  async function buscarCpf(raw) {
    if (edit?.[idKey]) return  // em edição não checa duplicado
    const n = (raw || '').replace(/\D/g, '')
    if (n.length < 11) { setCpfInfo(null); return }
    if (!entidade) return
    try {
      const { data } = await api.get(`/pessoas/buscar?cpf=${n}`)
      const mesmo = data[entidade]
      const outro = entidade === 'cliente' ? data.licenciado : data.cliente
      if (mesmo) {
        setCpfInfo({ tipo: 'erro', msg: `Já existe ${entidade} com este CPF/CNPJ: ${mesmo.nome}.`,
          btn: { label: 'Abrir cadastro existente', fn: () => { setEdit(mesmo); setCpfInfo(null) } } })
      } else if (outro) {
        const t = entidade === 'cliente' ? 'licenciado' : 'cliente'
        setCpfInfo({ tipo: 'aviso', msg: `Esta pessoa já é ${t}: ${outro.nome}.`,
          btn: { label: 'Usar os dados', fn: () => { prefillPessoa(outro); setCpfInfo({ tipo: 'ok', msg: 'Dados carregados ✓' }) } } })
      } else setCpfInfo(null)
    } catch { setCpfInfo(null) }
  }

  const fpQuery = Object.entries(fixedParams).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join('')

  async function load(p = 1, term = search, sz = size) {
    try {
      const q = searchable && term ? `&search=${encodeURIComponent(term)}` : ''
      const { data } = await api.get(`${endpoint}?page=${p}&limit=${sz}${q}${fpQuery}`)
      if (Array.isArray(data)) {
        setRows(data); setMeta({ total: data.length, pages: 1 })
      } else {
        const arr = data[listKey] || data.data || []
        const total = data.total != null ? data.total : arr.length
        setRows(arr)
        setMeta({ total, pages: data.pages || Math.max(1, Math.ceil(total / sz)) })
      }
      setPage(p)
    } catch {
      setRows([]); setMeta({ total: 0, pages: 1 })
    }
  }
  const mudarTamanho = (n) => { setSize(n); load(1, search, n) }
  // Recarrega ao trocar de tela (endpoint+filtro) — evita dados da tela anterior.
  useEffect(() => { setSearch(''); load(1, '') }, [endpoint, fpQuery]) // eslint-disable-line

  // Carrega opções de selects que vêm de um endpoint (f.optionsUrl).
  useEffect(() => {
    const dyn = fields.filter((f) => f.optionsUrl)
    if (!dyn.length) return
    Promise.all(dyn.map((f) => api.get(f.optionsUrl).then((r) => {
      const arr = r.data?.data || r.data || []
      const opts = [{ value: '', label: f.optionsEmpty || '— nenhum —' }].concat(
        arr.map((it) => ({ value: it[f.optionValue || 'id'], label: it[f.optionLabel || 'nome'] })))
      return [f.key, opts]
    }).catch(() => [f.key, []]))).then((pairs) => setDynOpts(Object.fromEntries(pairs)))
  }, [endpoint]) // eslint-disable-line

  function buscar() { load(1, search) }

  function novo() {
    const b = {}
    fields.forEach((f) => {
      b[f.key] = f.default !== undefined ? f.default : (f.type === 'checkbox' ? false : (f.type === 'campos' || f.type === 'itens') ? [] : '')
    })
    setEdit(b); setErro(''); setCepMsg(''); setCpfInfo(null)
  }

  async function salvar() {
    setErro('')
    const novo = !edit[idKey]
    const body = { ...fixedParams }
    fields.forEach((f) => {
      let v = edit[f.key]
      if (f.type === 'number' && v !== '' && v != null) v = Number(v)
      if (f.type === 'checkbox') { body[f.key] = !!v; return }
      if (v !== '' && v != null) body[f.key] = v
    })
    try {
      if (edit[idKey]) await api.put(`${endpoint}/${edit[idKey]}`, body)
      else await api.post(endpoint, body)
      setEdit(null); load(page)
      successModal(novo ? 'Registro criado com sucesso!' : 'Registro atualizado com sucesso!')
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Erro ao salvar')
    }
  }

  async function excluir(id) {
    if (!(await confirmModal('Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.', { title: 'Excluir registro', danger: true, okLabel: 'Excluir' }))) return
    try { await api.delete(`${endpoint}/${id}`); load(page); successModal('Registro excluído.') } catch { /* noop */ }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-heading font-bold text-gray-900">{title}</h1>
        <div className="flex items-center gap-2">
          {searchable && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 focus-within:border-accent-400 transition-colors">
              <Search size={15} className="text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && buscar()} placeholder="Buscar…"
                className="py-2 text-sm outline-none bg-transparent w-44" />
            </div>
          )}
          {!readOnly && (
            <button onClick={novo}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-semibold shadow-md hover:brightness-95 transition-all">
              <Plus size={16} /> Novo
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-4 py-3 whitespace-nowrap font-semibold">{c.label}</th>)}
              {!readOnly && <th className="px-4 py-3 text-right font-semibold">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[idKey]} className="border-t border-gray-100 hover:bg-primary-50/30 transition-colors">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-gray-700">
                    {c.type === 'imagem'
                      ? (row[c.key]
                          ? <img src={row[c.key]} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-100" />
                          : <span className="text-gray-300">—</span>)
                      : c.type === 'whatsapp'
                        ? (row[c.key]
                            ? <a href={`https://wa.me/55${String(row[c.key]).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-emerald-600 font-medium hover:underline"><MessageCircle size={14} /> {row[c.key]}</a>
                            : <span className="text-gray-300">—</span>)
                        : c.render ? c.render(row) : (row[c.key] ?? '—')}
                  </td>
                ))}
                {!readOnly && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEdit(row); setErro(''); setCepMsg(''); setCpfInfo(null) }} title="Editar"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-accent-600 hover:bg-accent-50 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => excluir(row[idKey])} title="Excluir"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-400">Nenhum registro.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={size} onPage={(p) => load(p)} onPageSize={mudarTamanho} />

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto tc-scroll">
            <h2 className="text-lg font-heading font-bold text-gray-900">{edit[idKey] ? 'Editar' : 'Novo'} — {title}</h2>
            {erro && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{erro}</p>}
            {visibleFields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                {f.type === 'imagem' ? (
                  <ImageField value={edit[f.key] || ''} onChange={(v) => setEdit({ ...edit, [f.key]: v })} />
                ) : f.type === 'campos' ? (
                  <CamposBuilder value={edit[f.key] || []} onChange={(v) => setEdit({ ...edit, [f.key]: v })} />
                ) : f.type === 'itens' ? (
                  <ItensBuilder value={edit[f.key] || []} onChange={(v) => setEdit({ ...edit, [f.key]: v })}
                    subcampos={f.subcampos || []} addLabel={f.addLabel || 'Adicionar item'} />
                ) : f.type === 'select' ? (
                  <SearchableSelect value={edit[f.key] ?? ''} onChange={(v) => setEdit({ ...edit, [f.key]: v })}
                    options={f.options || dynOpts[f.key] || []} placeholder="Selecione..." />
                ) : f.type === 'checkbox' ? (
                  <input type="checkbox" checked={!!edit[f.key]} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.checked })} className="w-4 h-4 accent-accent-500" />
                ) : f.type === 'cpf' ? (
                  <div>
                    <input type="text" value={edit[f.key] ?? ''} placeholder="000.000.000-00"
                      onChange={(e) => { setEdit({ ...edit, [f.key]: e.target.value }); setCpfInfo(null) }}
                      onBlur={(e) => buscarCpf(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-accent-400" />
                    {cpfInfo && (
                      <div className={`mt-1 text-xs rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2 ${cpfInfo.tipo === 'erro' ? 'bg-red-50 text-red-700' : cpfInfo.tipo === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        <span>{cpfInfo.msg}</span>
                        {cpfInfo.btn && <button type="button" onClick={cpfInfo.btn.fn} className="font-semibold underline whitespace-nowrap">{cpfInfo.btn.label}</button>}
                      </div>
                    )}
                  </div>
                ) : f.type === 'cep' ? (
                  <div>
                    <input type="text" value={edit[f.key] ?? ''} placeholder="00000-000"
                      onChange={(e) => { const v = e.target.value; setEdit({ ...edit, [f.key]: v }); setCepMsg(''); if (v.replace(/\D/g, '').length === 8) buscarCep(v) }}
                      onBlur={(e) => buscarCep(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-accent-400" />
                    {cepMsg && <span className={`text-[11px] mt-0.5 block ${cepMsg.includes('✓') ? 'text-emerald-600' : 'text-gray-400'}`}>{cepMsg}</span>}
                  </div>
                ) : f.type === 'textarea' ? (
                  <textarea value={edit[f.key] ?? ''} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-accent-400" rows={3} />
                ) : (
                  <input type={f.type || 'text'} value={edit[f.key] ?? ''} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-accent-400" />
                )}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEdit(null)} className="px-4 py-2 text-gray-500 hover:text-gray-700">Cancelar</button>
              <button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow-md hover:brightness-95">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
